import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { Subject, take } from "rxjs";
import { ApiService, Countries, Mover, Settings } from "./api.service";
import { TrackingService } from './tracking.service';
import { CookieService } from 'ngx-cookie-service';
import { environment } from "../../environments/environment";
import domainData from '../../assets/json/environment.json';
import countryData from '../../assets/json/environment-by-country.json';
import Bugsnag from "@bugsnag/js";

@Injectable({
    providedIn: 'root'
})
export class FormService {

	public formActive : boolean = true;
	public formLoading : boolean = false;
	public fccLoading : boolean = false;
	public questionsActive : boolean = false;
	public questions : any | undefined;
	public aboutToRedirect : boolean = false;
	public failSafeMessage : boolean = false;
	public dashboardUrl : string = '';
	public token : string = '';
	public showNote : boolean = false;
	public countries !: Countries;
	public popularCountries !: Countries;
	public settings !: Settings;
	public homeUrl!: string;
	public beforeUnloadListener: any;
	public domainData: any;
	public vcCategories: any | undefined;
	public movingToExtendedOptionalLabelsStartingValue = false;
	public aiMistralDone = false;

	// Initial steps amount | Don't count dynamic steps
	public totalStepsAmount: number = 6;

	// The current step number
	public currentStep : number = 1;
	public currentFormGroup : UntypedFormGroup;
	public currentFormGroupName : string = 'movingFrom';

	// The state of the form
	public isSubmitting = false;

	// The current state of the progress bar
	public progress : number = 15;
    subject = new Subject<any>();
    public mainForm: UntypedFormGroup = new UntypedFormGroup({
        movingFrom: new UntypedFormGroup({
            address: new UntypedFormControl('', [Validators.required]),
            city: new UntypedFormControl('', [Validators.required]),
            postcode: new UntypedFormControl('', [Validators.required]),
            country: new UntypedFormControl(null, [Validators.required]),
	        _houseNumber: new UntypedFormControl( '' ), // This is for the postcode api
            _progress: new UntypedFormControl(15),
            _next: new UntypedFormControl('movingTo'),
	        _previous: new UntypedFormControl( false ),
            _doneTracking: new UntypedFormControl( true ),
            _shouldShow: new UntypedFormControl(true),
	        _initiallyFocusAddress: new UntypedFormControl( false ),
	        _googleApiUsed: new UntypedFormControl( false )
        }),
	    propertyTypeFrom: new UntypedFormGroup({
			propertyType: new UntypedFormControl( '' ),
		    m2: new UntypedFormControl( '' ),
		    propertyRooms: new UntypedFormControl( '' ),
		    _progress: new UntypedFormControl(20),
		    _next: new UntypedFormControl( 'movingTo' ),
		    _previous: new UntypedFormControl( 'movingFrom' ),
		    _doneTracking: new UntypedFormControl( false ),
		    _shouldShow: new UntypedFormControl(false ),
	    }),
	    // Only shown if propertyTypeFrom.propertyType = apartment/flat
	    propertyTypeDetailsFrom: new UntypedFormGroup( {
		    floor: new UntypedFormControl( '' ),
		    elevator: new UntypedFormControl( '' ),
		    _progress: new UntypedFormControl(25),
		    _next: new UntypedFormControl( 'movingTo' ),
		    _previous: new UntypedFormControl( 'propertyTypeFrom' ),
		    _doneTracking: new UntypedFormControl( false ),
		    _shouldShow: new UntypedFormControl(false ),
	    }),
        movingTo: new UntypedFormGroup({
            city: new UntypedFormControl('', [Validators.required]),
            country: new UntypedFormControl(null, [Validators.required]),
	        address: new UntypedFormControl( '' ),
	        postcode: new UntypedFormControl( '' ),
	        _isExtended: new UntypedFormControl( false ),
	        _extendedOptional: new UntypedFormControl( false ),
            _progress: new UntypedFormControl(40),
            _next: new UntypedFormControl('movingSize'),
	        _previous: new UntypedFormControl( 'movingFrom' ),
	        _doneTracking: new UntypedFormControl( false ),
            _shouldShow: new UntypedFormControl( false ),
	        _googleApiUsed: new UntypedFormControl( false )
        }),
	    propertyTypeTo: new UntypedFormGroup({
		    propertyType: new UntypedFormControl( '' ),
		    _progress: new UntypedFormControl(45),
		    _next: new UntypedFormControl( 'movingSize' ),
		    _previous: new UntypedFormControl( 'movingTo' ),
		    _doneTracking: new UntypedFormControl( false ),
		    _shouldShow: new UntypedFormControl(false ),
	    }),
	    // Only shown if propertyTypeFrom.propertyType = apartment/flat
	    propertyTypeDetailsTo: new UntypedFormGroup( {
		    floor: new UntypedFormControl( '' ),
		    elevator: new UntypedFormControl( '' ),
		    _progress: new UntypedFormControl(50),
		    _next: new UntypedFormControl( 'movingSize' ),
		    _previous: new UntypedFormControl( 'propertyTypeTo' ),
		    _doneTracking: new UntypedFormControl( false ),
		    _shouldShow: new UntypedFormControl(false ),
	    }),
        movingSize: new UntypedFormGroup({
            movingSize: new UntypedFormControl('', [Validators.required]),
	        _bedroomsActive: new UntypedFormControl( false ),
            _progress: new UntypedFormControl(55),
            _next: new UntypedFormControl('movingContent'),
	        _previous: new UntypedFormControl( 'movingTo' ),
	        _doneTracking: new UntypedFormControl( false ),
            _shouldShow: new UntypedFormControl(false),
        }),
        movingContent: new UntypedFormGroup({
            movingContent: new UntypedFormControl('' ),
	        volumeM3: new UntypedFormControl( 0 ),
	        volumeFt3: new UntypedFormControl( 0 ),
	        boxesSmall: new UntypedFormControl( 0 ),
	        boxesMedium: new UntypedFormControl( 0 ),
	        boxesLarge: new UntypedFormControl( 0 ),
	        customItems: new UntypedFormControl( '' ),
	        _volumeCalculator: new UntypedFormControl( ''  ),
            _progress: new UntypedFormControl(65),
            _next: new UntypedFormControl('movingDate'),
	        _previous: new UntypedFormControl( 'movingSize' ),
	        _doneTracking: new UntypedFormControl( false ),
            _shouldShow: new UntypedFormControl(false),
        }),
	    movingBoxes: new UntypedFormGroup({
		    boxesSmall: new UntypedFormControl( 0 ),
		    boxesMedium: new UntypedFormControl( 0 ),
		    boxesLarge: new UntypedFormControl( 0 ),
		    customItems: new UntypedFormControl( '' ),
		    _progress: new UntypedFormControl(65),
		    _next: new UntypedFormControl('movingDate'),
		    _previous: new UntypedFormControl( 'movingSize' ),
		    _doneTracking: new UntypedFormControl( false ),
		    _shouldShow: new UntypedFormControl(false),
	    }),
        movingDate: new UntypedFormGroup({
            movingDate: new UntypedFormControl('', [Validators.required]),
            _progress: new UntypedFormControl(80),
            _next: new UntypedFormControl('nameEmailPhone'),
	        _previous: new UntypedFormControl( 'movingContent' ),
	        _shouldShow: new UntypedFormControl(false),
	        _doneTracking: new UntypedFormControl( false ),
        }),
	    consentMovers: new UntypedFormGroup({
		    movers: new UntypedFormArray([]),
		    _originalMovers: new UntypedFormArray([]),
		    _progress: new UntypedFormControl(85),
		    _next: new UntypedFormControl('nameEmailPhone'),
		    _previous: new UntypedFormControl('movingDate'),
		    _shouldShow: new UntypedFormControl(false),
		    _doneTracking: new UntypedFormControl(false)
	    }),
		nameEmailPhone: new UntypedFormGroup({
			name: new UntypedFormControl( '', [Validators.required]),
			phoneNumber: new UntypedFormControl('', [Validators.required]),
			phoneNumberPlaceholder: new UntypedFormControl('', []),
			phoneNumberComplete: new UntypedFormControl( '' ),
			email: new UntypedFormControl('', [Validators.required]),
			nonExclusiveLead: new UntypedFormControl( true ),
			_progress: new UntypedFormControl(90),
			_next: new UntypedFormControl('finished'),
			_previous: new UntypedFormControl( 'movingDate' ),
			_shouldShow: new UntypedFormControl(false ),
			_doneTracking: new UntypedFormControl( false ),
		}),
	    // UTM tags or Cookies
	    _ca: new UntypedFormControl( '' ),
	    _so: new UntypedFormControl( '' ),
	    _gclid: new UntypedFormControl( '' ),
	    _msclkid: new UntypedFormControl( '' ),
	    _lastpage: new UntypedFormControl( '' ),
	    _vcToken: new UntypedFormControl( '' ),
	    _customerId: new UntypedFormControl( '' ),
	    _customerName: new UntypedFormControl( '' ),
	    _language: new UntypedFormControl( this.locale.split('-')[0] ),

	    _isIframe: new UntypedFormControl( false ),

	    // Not used?
	    _exclusiveLead: new UntypedFormControl( '' ),

	    _propertyTypeFromEnabled: new UntypedFormControl( false  ),
	    _propertyTypeDetailsFromEnabled: new UntypedFormControl( false ),
	    _propertyTypeToEnabled: new UntypedFormControl( false  ),
	    _propertyTypeDetailsToEnabled: new UntypedFormControl( false ),

	    _movingContentOrMovingBoxes: new UntypedFormControl( '' ),

	    _m2Enabled: new UntypedFormControl( false ),
	    _propertyRoomsEnabled: new UntypedFormControl( false ),
	    _consentMoversEnabled: new UntypedFormControl( false ),
	    _mistralRemarksClarification: new UntypedFormControl( '' ),
	    _mistralRemarksReasoning: new UntypedFormControl( '' ),
	    _mistralVolumeRemarksClarification: new UntypedFormControl( '' ),
	    _mistralVolumeRemarksReasoning: new UntypedFormControl( '' ),
	    _mistralRemarksDuration: new UntypedFormControl( 0 ),

	    _fccEnabled: new UntypedFormControl( false ),
    });
	public thirdPartyForm: UntypedFormGroup = new UntypedFormGroup( {
		// Dynamic controls based on what questions we need to show
	});
	public volumeCalculatorForm: UntypedFormGroup = new UntypedFormGroup( {
		// Dynamic controls based on what the api gives us
	});

    constructor(private apiService: ApiService,
                private trackingService: TrackingService,
                private cookieService: CookieService,
				@Inject(LOCALE_ID) public locale: string
    ) {

		// Set initial form group
		this.currentFormGroup = this.mainForm.get( 'movingFrom' ) as UntypedFormGroup;

        // Services with injectable can't do their own ngOnInit
        this.init();
    }

    init(): void {

	    let hostname = window.location.hostname;
	    let domain = hostname.replace( 'www.', '' );

		if( ! environment.production && ! environment.staging ) {

			domain = 'sea.localhost';
		}

		// Subdomains to main
		if( domain.includes( 'sirelo.org' ) && ! domainData.hasOwnProperty( domain ) ) {

			domain = 'sirelo.org';
		}

		this.domainData = domainData[domain as keyof typeof domainData];

		// Get popular countries
	    this.apiService.getPopularCountries().subscribe( response => {
		    this.popularCountries = <Countries>response;
	    });

		// Get all countries
	    this.apiService.getCountries().subscribe( response => {
		    this.countries = <Countries>response;
	    } );

		// Get application settings
	    this.apiService.getAppSettings().subscribe( response => {

			this.settings = <Settings>response;

		    if( ! environment.production && ! environment.staging ) {
			    console.log( this.settings );
		    }

			this.homeUrl = this.settings.home_url;
	    } );

		// For all step0 Data
		this.handleStep0Data();

		// Check for prefill country
	    this.checkForPrefillValues();
    }

	// This function gets executed on the start of the form
	public handleStep0Data() {

		var movingFrom = this.mainForm.get( 'movingFrom' ) as UntypedFormGroup;
		var movingTo = this.mainForm.get( 'movingTo' ) as UntypedFormGroup;

		const queryString = window.location.search;
		const urlParams = new URLSearchParams(queryString);

		var placeFrom = urlParams.get( 'place_from' );
		var placeTo = urlParams.get( 'place_to' );

		const hasValue = (varToCheck:any) => {

			return varToCheck && varToCheck.length > 0;
		};

		if( placeFrom && placeFrom.length > 0 ) {

			// At this point we know that the Google api was used on step 0
			movingFrom.controls['_googleApiUsed'].setValue( true );

			var addressFrom = urlParams.get( 'route_from' );
			var streetNumberFrom = urlParams.get( 'street_number_from' );
			var postalCodeFrom = urlParams.get( 'postal_code_from' );
			var localityFrom = urlParams.get( 'locality_from' );
			var postalTownFrom = urlParams.get( 'postal_town_from' );
			var countryFrom = urlParams.get( 'country_from' );

			// Attempt to get AddressFrom from the url
			if( hasValue(addressFrom) ) {

				// Add HouseNumber to Street with space or add a space after the street
				addressFrom += hasValue(streetNumberFrom) ? ' '+streetNumberFrom : ' ';

				movingFrom.controls['address'].setValue( addressFrom );
			}

			// Attempt to get postcodeFrom from the url
			if( hasValue(postalCodeFrom) ) {

				movingFrom.controls['postcode'].setValue( postalCodeFrom );
			}

			// Attempt to get cityFrom/localityFrom from the url
			if( hasValue(localityFrom) ) {

				movingFrom.controls['city'].setValue( localityFrom );
			}
			else if( hasValue(postalTownFrom) ) {

				// Get the cityFrom from the postalTownFrom instead
				movingFrom.controls['city'].setValue( postalTownFrom );
			}

			// Attempt to get the countryFrom from the url
			if( hasValue(countryFrom) ) {

				movingFrom.controls['country'].setValue( countryFrom );
				movingFrom.controls['country'].markAsDirty();
			}

			// Has a postcode
			// Has a city
			// Has a country
			// Is missing the whole address or just the streetNumber
			if( hasValue(postalCodeFrom) && hasValue(localityFrom) && hasValue(countryFrom) &&
				( ! hasValue( addressFrom ) || ! hasValue( streetNumberFrom ) ) ) {

				// Set focus as either address or houseNumber is still missing
				movingFrom.controls['_initiallyFocusAddress'].setValue( true );
			}
		}

		if( placeTo && placeTo.length > 0 ) {

			// At this point we know that the Google api was used on step 0
			movingTo.controls['_googleApiUsed'].setValue( true );

			var addressTo = urlParams.get( 'route_to' );
			var streetNumberTo = urlParams.get( 'street_number_to' );
			var postalCodeTo = urlParams.get( 'postal_code_to' );
			var localityTo = urlParams.get( 'locality_to' );
			var postalTownTo = urlParams.get( 'postal_town_to' );
			var countryTo = urlParams.get( 'country_to' );

			// Attempt to get AddressTo from the url
			if( hasValue(addressTo) ) {

				// Add HouseNumber to Street with space or add a space after the street
				addressTo += hasValue(streetNumberTo) ? ' '+streetNumberTo : ' ';

				movingTo.controls['address'].setValue( addressTo );
				movingTo.controls['_isExtended'].setValue( true );
			}

			// Attempt to get postcodeTo from the url
			if( hasValue(postalCodeTo) ) {

				movingTo.controls['postcode'].setValue( postalCodeTo );
				movingTo.controls['_isExtended'].setValue( true );
			}

			// Attempt to get cityTo/localityTo from the url
			if( hasValue(localityTo) ) {

				movingTo.controls['city'].setValue( localityTo );
			}
			else if( hasValue(postalTownTo) ) {

				// Get the cityTo from the postalTownTo instead
				movingTo.controls['city'].setValue( postalTownTo );
			}

			// Attempt to get the countryTo from the url
			if( hasValue(countryTo) ) {

				movingTo.controls['country'].setValue( countryTo );
				movingTo.controls['country'].markAsDirty();
			}
		}

		var paramSo = urlParams.get( 'so' );
		var paramCa = urlParams.get( 'ca' );
		var paramGclid = urlParams.get( 'gclid' );
		var paramMsclkid = urlParams.get( 'msclkid' );

		// If the "so" parameter is set, use it
		if( hasValue(paramSo) ) {

			(<UntypedFormGroup>this.mainForm ).controls['_so'].setValue( paramSo );
		}
		else {

			const soCookieValue = this.cookieService.get('socookie');

			if( hasValue(soCookieValue) ) {

				// Set the Cookie value
				(<UntypedFormGroup>this.mainForm ).controls['_so'].setValue( soCookieValue );
			}
		}

		// If the "ca" parameter is set, use it
		if( hasValue(paramCa) ) {

			(<UntypedFormGroup>this.mainForm ).controls['_ca'].setValue( paramCa );
		}
		else {

			const caCookieValue = this.cookieService.get('cacookie');

			if( hasValue(caCookieValue) ) {

				// Set the Cookie value
				(<UntypedFormGroup>this.mainForm ).controls['_ca'].setValue( caCookieValue );
			}
		}

		// Get gclid from url
		if( hasValue(paramGclid) ) {

			(<UntypedFormGroup>this.mainForm ).controls['_gclid'].setValue( paramGclid );
		}
		else {

			// Get gclid from cookie
			const gclidCookieValue = this.cookieService.get('gclid');

			if( hasValue(gclidCookieValue) ) {

				(<UntypedFormGroup>this.mainForm ).controls['_gclid'].setValue( gclidCookieValue );
			}
		}

		// Get msclkid from url
		if( hasValue(paramMsclkid) ) {

			(<UntypedFormGroup>this.mainForm ).controls['_msclkid'].setValue( paramMsclkid );
		}
		else {

			// Get msclkid from cookie
			const msclkidCookieValue = this.cookieService.get('msclkid');

			if( hasValue(msclkidCookieValue) ) {

				(<UntypedFormGroup>this.mainForm ).controls['_msclkid'].setValue( msclkidCookieValue );
			}
		}

		// Get lastpage from cookie
		const lastpageCookieValue = this.cookieService.get('lastpage');

		if( hasValue(lastpageCookieValue) ) {

			// Pass it to the ERP
			(<UntypedFormGroup>this.mainForm ).controls['_lastpage'].setValue( lastpageCookieValue );
		}

		var paramVcToken = urlParams.get( 'vc' );

		if( hasValue(paramVcToken) ) {

			(<UntypedFormGroup>this.mainForm ).controls['_vcToken'].setValue( paramVcToken );
		}

		var paramCustomer = urlParams.get( 'customer' );

		if( hasValue(paramCustomer) ) {

			// Expected output
			interface CustomerResponse {
				origin_id: number;
				company_name: string;
			}

			// Get customer from the API
			this.apiService.getCustomer( paramCustomer! ).subscribe( (response: CustomerResponse) => {

				if( response ) {

					(<UntypedFormGroup>this.mainForm ).controls['_customerId'].setValue( response.origin_id );
					(<UntypedFormGroup>this.mainForm ).controls['_customerName'].setValue( response.company_name );

					const bodyTag = document.body;
					bodyTag.classList.add('reduced-spacing-last-step');
				}
			} );
		}

		var paramInPage = urlParams.get( 'in-page' );

		if( hasValue(paramInPage) ) {

			const html = document.getElementsByTagName('html')[0];
			html.classList.add('in-page');
			let node: any = document.createElement('script');
			node.src = 'https://cdnjs.cloudflare.com/ajax/libs/iframe-resizer/4.3.2/iframeResizer.contentWindow.min.js';
			node.type = 'text/javascript';
			node.async = true;
			node.charset = 'utf-8';
			document.getElementsByTagName('head')[0].appendChild(node);

			(<UntypedFormGroup>this.mainForm ).controls['_isIframe'].setValue( true );

			// Don't focus address just in case
			(<UntypedFormGroup>this.mainForm.controls['movingFrom'] ).controls['_initiallyFocusAddress'].setValue( false );
		}
		// If not in page
		else {

			// Make the browser give a confirmation alert before the user can close this page
			// This gets removed again when the users arrive at the thank-you message

			this.launchExitConfirmation();
		}

		// This needs to go before PropertyRoomsFieldCheck
		// Because the rooms field check needs to know if the property type is enabled or not
		this.initialPropertyTypeCheck();

		// This checks for the env value for movingTo to be extended
		// Now separated from propertyTypeCheck :)
		this.initialMovingToExtendedCheck();

		// Check the data from step 0 and if the field is enabled
		this.initialM2FieldCheck();

		// Check if we can enable the house size field
		this.initialPropertyRoomsFieldCheck();
	}

	// Submitting of a form group
    public submitFormGroup( formGroup: UntypedFormGroup ) {

		formGroup = this.currentFormGroup;

	    // If valid
        if( ! formGroup.valid ) {

            return;
        }

		// Clear note
	    this.showNote = false;

		// Not the submit button click
	    if( formGroup.controls['_next'].value !== 'finished' ) {

			// === DYNAMIC STEPS START === //
			// Here we can change the previous and next step based on values
		    // Don't change steps after this part

			this.verifyDynamicSteps();

			// === END OF DYNAMIC STEPS === //

		    // Get the name of the next form Group
		    var nextFormGroupName = formGroup.controls['_next'].value;

		    // Get next form group
			var nextFormGroup = (<UntypedFormGroup>this.mainForm.controls[nextFormGroupName]);

			// Add one to current step / causes animation
			this.currentStep++;
			this.currentFormGroup = nextFormGroup;
			this.currentFormGroupName = nextFormGroupName;

			// Hide old step
			formGroup.controls['_shouldShow'].setValue( false );

			// Show new step
			nextFormGroup.controls['_shouldShow'].setValue( true );

			// Scroll to top of window
			window.scrollTo(0,0);

			// For when the form is in an iframe
			if( 'parentIFrame' in window ) {

				// @ts-ignore
				parentIFrame.sendMessage( 'scroll-to-top-of-iframe' );
			}

			// Emit an event for any other code to use the new step name to do things
		    // This is also done in going back to previous steps in backMainForm
		    this.emitStepChange( this.currentFormGroupName );

			// Do tracking step change
		    // We do the tracking for viewing the step (not for completing it)
			// Only do tracking once per step
			if( ! nextFormGroup.controls['_doneTracking'].value ) {

				// Set progress to old/current progress percentage value
				this.progress = nextFormGroup.controls['_progress'].value

				this.trackingService.setStepWithName( nextFormGroupName );

				nextFormGroup.controls['_doneTracking'].setValue( true );
			}
		}
		else {

			this.progress = 100;
		}
    }

	// Submitting of the whole form
	public submitMainForm() {

		// Testing purposes
		if( ! environment.production ) {

			console.log( 'form values', this.mainForm.value );
		}

		// Check if fields are valid
		if( ! this.mainForm.valid ) {

			// Do something to show the error
			(<UntypedFormGroup>this.mainForm).markAllAsTouched();

			return;
		}

		// Already busy
		if( this.isSubmitting ) {

			return;
		}

		// Set to busy
		this.isSubmitting = true;

		// Set next form group
		this.currentFormGroupName = 'finished';

		// Add the data to any potential bugsnag report
		Bugsnag.addMetadata('Lead', this.mainForm.value );

		// Put the request in here
		var body = JSON.stringify( this.mainForm.value );

		// Start loading
		this.formLoading = true;

		// Show loading animation here?
		setTimeout( () => {

			// Show thank you page after 1 seconds
			this.formActive = false;
			this.formLoading = false;

			// Remove before unload event
			removeEventListener("beforeunload", this.beforeUnloadListener, {capture: true});

			// Check if we are an iframe
			if( 'parentIFrame' in window ) {

				// @ts-ignore
				parentIFrame.sendMessage( 'scroll-to-top-of-iframe' );
			}
		}, 1000 );

		// Post to super-form
		this.apiService.postRequest(body).subscribe(response => {

			//@TODO: Move fake loading to real loading


			if( ! environment.production ) {

				console.log( 'raw response: '  );
				console.log( response );
			}

			// Parse response
			response = JSON.parse( response );

			if( response === 'missing_required_fields' ) {

				// Don't need to do more
				return;
			}

			// Testing purposes
			if( ! environment.production ) {

				console.log( 'parsed response: '  );
				console.log( response );
			}

			// If successful
			if( 'token' in response && response.token.length > 0 ) {

				this.token = response.token;

				// Get moving Size for statistics
				var movingSize = (<UntypedFormGroup>this.mainForm.controls['movingSize']).controls['movingSize'].value;
				var movingFrom = (<UntypedFormGroup>this.mainForm.controls['movingFrom']).controls['country'].value;
				var movingTo = (<UntypedFormGroup>this.mainForm.controls['movingTo']).controls['country'].value;
				var paramCa = this.mainForm.controls['_ca'].value;
				var destinationType = ( movingFrom === movingTo ) ? 2 : 1;

				// Only do tracking when successful
				this.trackingService.onSubmitForm( response.token, movingSize, destinationType, paramCa );
			}

			// If there are questions in the response
			if( typeof response.questions !== null && typeof response.questions !== undefined && response.questions ) {

				// Make them loop-able for the code
				this.questions = Object.values( response.questions );

				// Initiate the third party form
				this.setupThirdPartyForm();
			}
			else {

				// No Third party questions
				// Attempt to redirect
				this.countDownAndRedirect();
			}

			this.isSubmitting = false;
		} );
	}

	private stepChangeSubject = new Subject<string>();
	stepChange$ = this.stepChangeSubject.asObservable();

	private emitStepChange( formGroupName: string) {

		this.stepChangeSubject.next( formGroupName );
	}

	// Setup for the third party questions
	public setupThirdPartyForm() {

		if( this.questions ) {

			var keys = [];

			// Loop over questions
			for( var question of this.questions ) {

				var questionKey = question.key;
				keys.push( question.key );

				// Add controls based on questions
				this.thirdPartyForm.addControl(
					'question_' + questionKey, new UntypedFormControl( '2' ) ) ;

				// Loop over the extra questions (max 2)
				for( var extraQuestion of [1,2] ) {

					var subQuestionKey = 'extra_' + extraQuestion;

					// Check if there is a sub question
					if( question[subQuestionKey + '_type' as keyof typeof question] !== "0" ) {

						var subQuestionName = 'question_' + questionKey + '_' + extraQuestion;
						this.thirdPartyForm.addControl( subQuestionName, new UntypedFormControl( '' ) );
					}
				}
			}

			this.thirdPartyForm.addControl( '_token', new UntypedFormControl( this.token ) ) ;
			this.thirdPartyForm.addControl( '_existingKeys', new UntypedFormControl( JSON.stringify( keys )))
			this.questionsActive = true;
			this.trackingService.setStepWithName( 'thirdPartyQuestions' );

			console.log( this.thirdPartyForm );
		}
	}

	// Setup for the volume calculator form
	public setupVolumeCalculatorForm( vcCategories: any ) {

		if( vcCategories ) {

			for( var [catIndex,category] of vcCategories.entries() ) {

				// Only index is used
				for( var [itemIndex, _] of category.items.entries() ) {

					var controlId = catIndex + '-' + itemIndex;

					// Add controls based on questions
					this.volumeCalculatorForm.addControl( controlId, new UntypedFormControl( 0 ) ) ;
				}
			}
		}
	}

	// To start the action of redirecting after x seconds if possible
	public countDownAndRedirect() {

		// We can only do something if we have a token
		if( typeof this.token.length === 'undefined' || this.token.length === 0 ) {

			console.warn( 'No token. Cannot Redirect' );
			this.failSafeMessage = true;
			return;
		}

		// The url is always domain + /personal-dashboard/
		// If we want to change this, We have to do the following
		// - Make a setting in WordPress where we can select the page that holds the personal dashboard
		// - Send this page url with the getAppSettings API and use that instead of the hardcoded version

		// Default url
		var dashboardUrl = 'https://sirelo.org/personal-dashboard/?id=' + this.token;

		// Find the url for the dashboard
		if( this.settings.has_dashboard ) {

			// This domain has its own dashboard. Use it instead
			dashboardUrl = window.location.origin + '/personal-dashboard/?id=' + this.token;
		}

		// Set dashboard url for the button and redirect
		this.dashboardUrl = dashboardUrl;

		// In seconds
		var waitingTime = 5;

		// Make sure the beforeunload is gone
		removeEventListener("beforeunload", this.beforeUnloadListener, {capture: true});

		console.log( 'Everything is in order. Starting count down for redirect to', this.dashboardUrl );

		// Show the message of redirecting the user shortly
		this.aboutToRedirect = true;

		setTimeout( function() {

			// Get token and redirect
			window.location.href = dashboardUrl;

		}, waitingTime * 1000 );
	}

	private verifyDynamicSteps() {

		// Check for enabling residence
		if( this.domainData?.residenceEnabled && ! this.domainData?.residenceIntEnabled ) {

			// The logic to show the propertyTypeDetailsFrom
			if( this.currentFormGroupName === 'propertyTypeFrom' ) {

				// This might change the next form group
				this.verifyPropertyTypeStep( 'from' );
			}

			if( this.currentFormGroupName === 'movingTo' ) {

				this.verifyMovingTo();
			}

			if( this.currentFormGroupName === 'propertyTypeTo' ) {

				// This might change the next form group
				this.verifyPropertyTypeStep( 'to' );
			}
		}

		// Check for movingContent or movingBoxes
		if( this.currentFormGroupName === 'movingSize' ) {

			this.verifyMovingSize();
		}

		if( this.domainData?.preMatchEnabled ) {

			// If next step
			if( this.currentFormGroup.controls['_next'].value === 'movingDate' ) {

				// Skip askPrematch if _customerId has a value
				if (!this.mainForm.controls['_customerId'].value) {
					// In this function we will check if we actually should do the prematch
					this.askPrematch();
				}
			}
		}

		if( this.currentFormGroupName === 'movingContent' ) {

			const movingContentFormGroup = (<UntypedFormGroup>this.mainForm.get( 'movingContent' ) );
			const remarksValue = movingContentFormGroup.get('movingContent')?.value;

			// Grab volume calculator items
			// Grab volume
			// Grab remarks
			// send to mistral AI

			const volumeCalculatorItems = movingContentFormGroup.get('_volumeCalculator')?.value;
			const volumeValue = movingContentFormGroup.get('volumeM3')?.value;

			// Combine volume calculator items and volume value and remarks into 1 variable
			const dataToSend = {
				volumeCalculatorItems,
				volumeValue,
				remarks: remarksValue,
			}

			this.dataToMistral( dataToSend );
		}
	}

	public back() {

		this.backMainForm();
	}

	public backMainForm() {

		var formGroup = this.currentFormGroup;

		// Clear this. just in case
		this.showNote = false;

		var prevFormGroupName = formGroup.controls['_previous'].value;
		var prevFormGroup = this.mainForm.get( prevFormGroupName ) as UntypedFormGroup;

		this.currentStep--;
		this.currentFormGroup = prevFormGroup;
		this.currentFormGroupName = prevFormGroupName;

		formGroup.controls['_shouldShow'].setValue( false );
		prevFormGroup.controls['_shouldShow'].setValue( true );

		window.scrollTo(0,0);

		if( 'parentIFrame' in window ) {

			// @ts-ignore
			parentIFrame.sendMessage( 'scroll-to-top-of-iframe' );
		}

		this.emitStepChange( this.currentFormGroupName );
	}

	// Function to show a specific component by formGroupName
    public shouldShow(formGroupName: string): boolean {

        return (<UntypedFormGroup>this.mainForm.controls[formGroupName]).controls['_shouldShow'].value;
    }

	private launchExitConfirmation() {

		// Store the listener
		this.beforeUnloadListener = (event: any ) => {
			event.preventDefault();

			// This is the same as the preventDefault for some browsers
			// This text is most likely not visible for anybody (or old browsers maybe)
			return event.returnValue = 'Are you sure you want to exit?';
		};

		var setupOnload = false;
		this.mainForm.valueChanges.subscribe( () => {

			if( ! setupOnload ) {

				setupOnload = true;
				addEventListener("beforeunload", this.beforeUnloadListener, {capture: true});
			}
		} );
	}

	public verifyPostcodeApi() {

		// Enabled for this domain or not
		if( ! this.domainData?.postcodeApiEnabled )
			return false;

		// Check if we got a postcode but nothing else

		var movingFrom = this.mainForm.get( 'movingFrom' ) as UntypedFormGroup;

		// Return if (or)
		// - Address has a value
		// - City has a value
		// - Country has a value and it's not NL
		var addressHasValue = movingFrom.controls['address'].value && movingFrom.controls['address'].value.length > 0;
		var cityHasValue = movingFrom.controls['city'].value && movingFrom.controls['city'].value.length > 0;
		var countryHasValueAndIsNotNetherlands = movingFrom.controls['country'].value && movingFrom.controls['country'].value.length > 0 && this.verifyCountryCode( movingFrom.controls['country'].value ) !== 'NL';

		if( addressHasValue || cityHasValue || countryHasValueAndIsNotNetherlands )
			return false;

		// We good, attempt to launch api

		return true;
	}

	/**
	 * Not used anymore since we are now going to use the property size question on the propertyTypeFrom step
	 * @private
	 */
	private checkBedrooms() {

		var $fromVal = this.verifyCountryCode( (<UntypedFormGroup>this.mainForm.controls['movingFrom']).controls['country'].value );
		var $toVal = this.verifyCountryCode( (<UntypedFormGroup>this.mainForm.controls['movingTo']).controls['country'].value );
		var $wasBedroomsActive = (<UntypedFormGroup>this.mainForm.controls['movingSize']).controls['_bedroomsActive'].value;
		var $isBedroomsActive = ( $fromVal === 'GB' && $toVal === 'GB' );
		var $didItChange = ( $wasBedroomsActive === $isBedroomsActive );

		// Set new value of bedrooms (no event)
		(<UntypedFormGroup>this.mainForm.controls['movingSize']).controls['_bedroomsActive'].setValue( $isBedroomsActive, {emitEvent:false} );

		// Check if the previous value of bedrooms was different from what it is now
		if( ! $didItChange ) {

			// Reset the movingSize field (no event)
			(<UntypedFormGroup>this.mainForm.controls['movingSize']).controls['movingSize'].reset( null,{emitEvent:false});
		}
	}

	public verifyCountryCode( countryCode: string ) : string {

		var alternativeCountryCodes = {
			'GB1' : 'GB', // England
			'GB2' : 'GB', // Scotland
			'GB3' : 'GB', // Wales
			'NL1' : 'NL', // Holland
			'CW1' : 'CW', // Netherlands Antilles
			'US1' : 'US',
		}

		if( countryCode in alternativeCountryCodes ) {

			return alternativeCountryCodes[countryCode as keyof typeof alternativeCountryCodes];
		}

		return countryCode;
	}

	public verifyTabIndex( formGroupName: string ) {

		// Check if form group is active
		return (<UntypedFormGroup>this.mainForm.get( formGroupName ) ).controls['_shouldShow'].value ? 0 : -1;
	}

	public autoFillProtection( formGroupName: string ) {

		return (<UntypedFormGroup>this.mainForm.get( formGroupName ) ).controls['_shouldShow'].value ? 'on' : 'absolutely-not';
	}

	/**
	 * Retrieves the correct order for the address fields on movingFrom and movingTo
	 * Can be requested specifically for a certain situation
	 * The specific situation relies on the environment file having the values
	 * If it's not there, it will fall back to the default
	 * @param specific
	 */
	public getFieldOrder( specific: string = 'both' ) {

		// Decide order based on domainData
		var fieldOrder = this.domainData?.addressFieldOrder;

		// Only if specific movingTo is requested
		if( specific === 'movingTo' ) {

			var fieldOrderTo = this.domainData?.addressFieldOrderTo;

			if( fieldOrderTo && fieldOrderTo.length > 0 ) {

				fieldOrder = fieldOrderTo;
			}
		}

		return fieldOrder ?? ['address', 'city', 'postcode'];
	}

	public getFieldOrderByCountry( country: string, specific: string = 'both' ) {

		var fieldOrder;

		// Clean country
		country = this.verifyCountryCode( country );

		// Grab country data
		var selectedCountryData = countryData[country as keyof typeof countryData];

		// Only when country first is enabled as this project is gone active together with country first
		// If there's no country data. Fallback
		if( ! this.domainData?.countryFirst || ! selectedCountryData ) {

			return this.getFieldOrder( specific );
		}

		// Default selection
		var keyToSelect = 'fieldOrder';

		// If the movingTo order is requested
		if( specific === 'movingTo' ) {

			// Grab field order
			keyToSelect = 'fieldOrderTo';

			fieldOrder = selectedCountryData[keyToSelect as keyof typeof selectedCountryData];

			console.log( 'selecting fieldOrderTo from country ' + country );
			console.log( fieldOrder );

			if( ! Array.isArray(fieldOrder) || fieldOrder.length <= 0 ) {

				console.log( 'fallback to field order' );

				fieldOrder = selectedCountryData["fieldOrder" as keyof typeof selectedCountryData];
			}
		}
		else {

			fieldOrder = selectedCountryData[keyToSelect as keyof typeof selectedCountryData];
		}

		console.log( 'Field order:', fieldOrder );

		// Check if that exists otherwise fallback
		return Array.isArray(fieldOrder) && fieldOrder.length > 0 ? fieldOrder : ['address', 'city', 'postcode'];
	}

	public getVolumeOrder() {

		var type = this.settings?.volume_type ?? 'both';

		if( type === 'both' ) {

			return ['m3','divider','ft3'];
		}
		else if( type === 'both_reversed' ) {

			return ['ft3','divider','m3'];
		}
		else if ( type === 'ft3' ) {

			return ['ft3'];
		}
		else {

			return ['m3'];
		}
	}

	public getFloorLevels(): any {

		return [
			{
				value: 1,
				label: $localize `:{floor-level-0}:0`,
			},
			{
				value: 5,
				label: '1',
			},
			{
				value: 6,
				label: '2',
			},
			{
				value: 7,
				label: '3',
			},
			{
				value: 8,
				label: '4',
			},
			{
				value: 9,
				label: '5',
			},
			{
				value: 11,
				label: '6+',
			},
		];
	}

	public getVariationTranslation( type: string ) {

		var output = '';

		if( type === 'label-address' ) {

			// Also variation 1 | default
			output = $localize `:{address-label}:Address`;

			if( this.locale.substring( 0, 2 ) === 'en' ) {

				// Check if there is a variation
				let addressVariation = this.domainData?.translationVariations?.en?.address;

				if( addressVariation ) {

					// List of possible variations
					// This should only include the variations
					// If one language file needs 2 variations
					// E.g. en-US uses both Address and Street address
					let variations = {
						// 1 Is default Address
						2: 'Street address',
						3: 'Halloo',
						// .. add more variations if needed
					}

					let searchVariation = variations?.[addressVariation as keyof typeof variations];

					if( addressVariation > 1 && searchVariation ) {

						output = searchVariation;
					}
				}
			}
		}
		else if( type === 'label-postcode' ) {

			// Also variation 1 | default
			output = $localize `:{postcode-label}:Postcode`;

			if( this.locale.substring( 0, 2 ) === 'en' ) {

				// Check if there is a variation
				let postcodeVariation = this.domainData?.translationVariations?.en?.postcode;

				if( postcodeVariation ) {

					// List of possible variations
					// This should only include the variations
					// If one language file needs 2 variations
					// E.g. en-US uses both Postcode and ZIP code/Post code
					let variations = {
						// 1 Is default Postcode
						2: 'ZIP code/Post code',
						3: 'Postal code',
						// .. add more variations if needed
					}

					let searchVariation = variations?.[postcodeVariation as keyof typeof variations];

					if( postcodeVariation > 1 && searchVariation ) {

						output = searchVariation;
					}
				}
			}
		}

		return output;
	}


	// === PROPERTY TYPE CODE === //

	private initialPropertyTypeCheck() {

		// ========================= //
		// CHECK FOR STEP0 RESIDENCE //
		// ========================= //

		// Check if we know this lead is international or not
		var fromVal = this.verifyCountryCode( (<UntypedFormGroup>this.mainForm.controls['movingFrom']).controls['country'].value ) ?? '';
		var toVal = this.verifyCountryCode( (<UntypedFormGroup>this.mainForm.controls['movingTo']).controls['country'].value ) ?? '';

		// There are values and they're not the same
		if( fromVal.length > 0 && toVal.length > 0 && fromVal !== toVal ) {

			// Known international from step 0

			// Disable residence mode
			this.domainData['residenceEnabled'] = false;
		}

		// Check if residence is enabled on this website
		if( this.domainData?.residenceEnabled ) {

			this.enablePropertyTypeFrom();
		}

		// ======================= //
		// CHECK FOR INT RESIDENCE //
		// ======================= //

		if( this.domainData?.residenceIntEnabled && ! this.domainData?.residenceEnabled ) {

			this.enablePropertyTypeFrom();
		}
	}

	// Check if we have to show the propertyTypeDetails step
	public verifyPropertyTypeStep( fromOrTo: string ) {

		// Set direction
		var direction = ( fromOrTo === 'from' ? 'From' : 'To' );

		// If propertyType is 2
		var propertyType = (<UntypedFormGroup>this.mainForm.get( 'propertyType' + direction ) ).controls['propertyType'].value;

		// Get current state
		var wasDetailsActive = this.mainForm.controls['_propertyTypeDetails' + direction + 'Enabled'].value;

		// If is apartment/flat and details were not active
		if( propertyType === '2' && wasDetailsActive === false ) {

			if( fromOrTo === 'from' ) {

				this.enablePropertyTypeDetailsFrom();
			}
			else {

				this.enablePropertyTypeDetailsTo();
			}
		}
		// property type house and details were active then reset stuff
		else if( propertyType === '1' && wasDetailsActive === true ) {

			if( fromOrTo === 'from' ) {

				this.disablePropertyTypeDetailsFrom();
			}
			else {

				this.disablePropertyTypeDetailsTo();
			}
		}
	}

	// This enables or disables propertyTypeTo
	private verifyMovingTo() {

		// Check if lead is national
		var fromVal = this.verifyCountryCode( (<UntypedFormGroup>this.mainForm.controls['movingFrom']).controls['country'].value );
		var toVal = this.verifyCountryCode( (<UntypedFormGroup>this.mainForm.controls['movingTo']).controls['country'].value );

		// Also check if movingTo street & zipcode have a value
		var movingToAddress = (<UntypedFormGroup>this.mainForm.controls['movingTo']).controls['address'].value ?? '';
		var movingToPostcode = (<UntypedFormGroup>this.mainForm.controls['movingTo']).controls['postcode'].value ?? '';
		var hasAddressAndPostcode = ( movingToAddress.length > 0 && movingToPostcode.length > 0 );

		var wasPropertyTypeToEnabled = (<UntypedFormGroup>this.mainForm ).controls['_propertyTypeToEnabled'].value;

		// National lead and if it has address and postcode
		if( fromVal === toVal && hasAddressAndPostcode ) {

			// If it wasn't enabled before
			if( ! wasPropertyTypeToEnabled ) {

				// Enable it
				this.enablePropertyTypeTo();
			}
		}
		else {

			// Check if it was enabled
			if( wasPropertyTypeToEnabled ) {

				// Disable propertyTypeTo
				this.disablePropertyTypeTo();
			}
		}
	}

	private enablePropertyTypeFrom() {

		console.log( 'Enable PropertyTypeFrom' );

		(<UntypedFormGroup>this.mainForm ).controls['_propertyTypeFromEnabled'].setValue( true );

		// Set next and prev for the steps before and after the property type from step
		(<UntypedFormGroup>this.mainForm.controls['movingFrom'] ).controls['_next'].setValue( 'propertyTypeFrom' );
		(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['_previous'].setValue( 'propertyTypeFrom' );

		if( ! this.domainData?.hidePropertyTypeQuestion ) {

			// Do not make propertyTypeQuestion required if it's not showing
			(<UntypedFormGroup>this.mainForm.controls['propertyTypeFrom'] ).controls['propertyType'].addValidators( Validators.required );
		}

		// Add one to total step amount
		this.totalStepsAmount++;
	}

	private enablePropertyTypeDetailsFrom() {

		console.log( 'Enable PropertyTypeDetailsFrom' );

		// Set next and prev for the steps before and after the property type from step
		(<UntypedFormGroup>this.mainForm.controls['propertyTypeFrom'] ).controls['_next'].setValue( 'propertyTypeDetailsFrom' );
		(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['_previous'].setValue( 'propertyTypeDetailsFrom' );

		// Add one to total step amount
		this.totalStepsAmount++;

		(<UntypedFormGroup>this.mainForm.controls['propertyTypeDetailsFrom'] ).controls['floor'].addValidators( Validators.required );

		this.mainForm.controls['_propertyTypeDetailsFromEnabled'].setValue( true );
	}

	private disablePropertyTypeDetailsFrom() {

		console.log( 'Disable PropertyTypeDetailsFrom' );

		// Reset next and prev for the steps before and after the property type from step
		(<UntypedFormGroup>this.mainForm.controls['propertyTypeFrom'] ).controls['_next'].setValue( 'movingTo' );
		(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['_previous'].setValue( 'propertyTypeFrom' );

		// Minus 1 total steps
		this.totalStepsAmount--;

		// Disable propertyTypeDetails
		this.mainForm.controls['_propertyTypeDetailsFromEnabled'].setValue( false );

		(<UntypedFormGroup>this.mainForm.controls['propertyTypeDetailsFrom'] ).controls['floor'].removeValidators( Validators.required );

		// Reset values for propertyTypeDetails
		(<UntypedFormGroup>this.mainForm.controls['propertyTypeDetailsFrom'] ).controls['floor'].reset();
		(<UntypedFormGroup>this.mainForm.controls['propertyTypeDetailsFrom'] ).controls['elevator'].reset();
	}

	private enablePropertyTypeTo() {

		console.log( 'Enable PropertyTypeTo' );

		(<UntypedFormGroup>this.mainForm ).controls['_propertyTypeToEnabled'].setValue( true );

		// Set next and prev for the steps before and after the property type from step
		(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['_next'].setValue( 'propertyTypeTo' );
		(<UntypedFormGroup>this.mainForm.controls['movingSize'] ).controls['_previous'].setValue( 'propertyTypeTo' );

		(<UntypedFormGroup>this.mainForm.controls['propertyTypeTo'] ).controls['propertyType'].addValidators( Validators.required );

		// Add one to total step amount
		this.totalStepsAmount++;
	}

	private disablePropertyTypeTo() {

		console.log( 'Disable PropertyTypeTo' );

		// Disable propertyTypeDetailsTo first
		this.disablePropertyTypeDetailsTo();

		// Disable the propertyTypeTo
		(<UntypedFormGroup>this.mainForm ).controls['_propertyTypeToEnabled'].setValue( false );

		// Reset next and prev
		(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['_next'].setValue( 'movingSize' );
		(<UntypedFormGroup>this.mainForm.controls['movingSize'] ).controls['_previous'].setValue( 'movingTo' );

		(<UntypedFormGroup>this.mainForm.controls['propertyTypeTo'] ).controls['propertyType'].removeValidators( Validators.required );

		// Subtract one from total steps
		this.totalStepsAmount--;
	}

	private enablePropertyTypeDetailsTo() {

		console.log( 'Enable PropertyTypeDetailsTo' );

		// Set next and prev for the steps before and after the property type from step
		(<UntypedFormGroup>this.mainForm.controls['propertyTypeTo'] ).controls['_next'].setValue( 'propertyTypeDetailsTo' );
		(<UntypedFormGroup>this.mainForm.controls['movingSize'] ).controls['_previous'].setValue( 'propertyTypeDetailsTo' );

		// Add one to total step amount
		this.totalStepsAmount++;

		(<UntypedFormGroup>this.mainForm.controls['propertyTypeDetailsTo'] ).controls['floor'].addValidators( Validators.required );

		this.mainForm.controls['_propertyTypeDetailsToEnabled'].setValue( true );
	}

	private disablePropertyTypeDetailsTo() {

		// If it was disabled already
		if( ! this.mainForm.controls['_propertyTypeDetailsToEnabled'].value ) {

			return;
		}

		console.log( 'Disable PropertyTypeDetailsTo' );

		// Reset next and prev for the steps before and after the property type from step
		(<UntypedFormGroup>this.mainForm.controls['propertyTypeTo'] ).controls['_next'].setValue( 'movingSize' );
		(<UntypedFormGroup>this.mainForm.controls['movingSize'] ).controls['_previous'].setValue( 'propertyTypeTo' );

		// Minus 1 total steps
		this.totalStepsAmount--;

		// Disable propertyTypeDetails
		this.mainForm.controls['_propertyTypeDetailsToEnabled'].setValue( false );

		(<UntypedFormGroup>this.mainForm.controls['propertyTypeDetailsTo'] ).controls['floor'].removeValidators( Validators.required );

		// Reset values for propertyTypeDetails
		(<UntypedFormGroup>this.mainForm.controls['propertyTypeDetailsTo'] ).controls['floor'].reset();
		(<UntypedFormGroup>this.mainForm.controls['propertyTypeDetailsTo'] ).controls['elevator'].reset();
	}

	// === MovingSize ===
	public verifyMovingSize() {

		var movingSize = (<UntypedFormGroup>this.mainForm.controls['movingSize'] );

		// Check what the next step will be
		if( movingSize.controls['movingSize'].value !== 4 ) {

			// Enable movingContent
			this.enableMovingContent();
		}
		else {

			// Enable movingBoxes
			this.enableMovingBoxes();
		}
	}

	private enableMovingContent() {

		// Set the next step for moving size
		(<UntypedFormGroup>this.mainForm.controls['movingSize'] ).controls['_next'].setValue( 'movingContent' );

		// Set the previous step for the movingDate step
		(<UntypedFormGroup>this.mainForm.controls['movingDate'] ).controls['_previous'].setValue( 'movingContent' );
	}

	private enableMovingBoxes() {

		// Set the next step for movingSize step
		(<UntypedFormGroup>this.mainForm.controls['movingSize'] ).controls['_next'].setValue( 'movingBoxes' );

		// Set the previous step for the movingDate step
		(<UntypedFormGroup>this.mainForm.controls['movingDate'] ).controls['_previous'].setValue( 'movingBoxes' );
	}

	// === M2 FIELD === //

	private initialM2FieldCheck() {

		// If propertyTypeFrom is not enabled
		// M2 can only be active if propertyType is enabled
		// PropertyTypeFrom will be enabled or disabled before this function starts.
		// propertyType will not be disabled again after it being enabled
		// We follow the same strategy for the m2 field
		if( ! this.mainForm.controls['_propertyTypeFromEnabled'].value ) {

			return;
		}

		if( this.domainData?.m2FieldEnabled ) {

			console.log( 'Enable `m2` field' );
			this.mainForm.controls['_m2Enabled'].setValue( true );
			(<UntypedFormGroup>this.mainForm.controls['propertyTypeFrom'] ).controls['m2'].addValidators( Validators.required );
		}
	}

	// === MovingTo Check for extended === //

	private initialMovingToExtendedCheck() {

		if( this.domainData?.movingToExtendedEnabled ) {

			var movingTo = this.mainForm.get( 'movingTo' ) as UntypedFormGroup;

			// If is not extended, meaning we have no data from step 0 for address and postcode
			if( ! movingTo.controls['_isExtended'].value ) {

				// Extend it but optional
				// Note: They are always optional, but with the _extendedOptional they also get (optional) in the label
				// They do not get the (optional) label in the case of being extended when step0 movingTo data is filled in
				movingTo.controls['_isExtended'].setValue( true );
				movingTo.controls['_extendedOptional'].setValue( true );
				this.movingToExtendedOptionalLabelsStartingValue = true;

				console.log( 'Enabling movingTo Extended' );
			}
		}
	}

	// === MovingTo fields extended checking === //

	public checkToEnableMovingToExtended() {

		// If country first is enabled
		if( this.domainData?.countryFirst ) {

			const makeMovingToRequiredOrOptional = ( what: string ) => {

				if( what === 'required' ) {

					// optional fields go required
					// When we make this optional again, we know the original value from movingToExtendedOptionalLabelsStartingValue
					movingTo.controls['_extendedOptional'].setValue( false );

					console.log( 'movingTo address & postcode REQUIRED' );

					(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['address'].addValidators( Validators.required );
					(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['address'].updateValueAndValidity();

					(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['postcode'].addValidators( Validators.required );
					(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['postcode'].updateValueAndValidity();
				}
				else {

					console.log( 'movingTo address & postcode OPTIONAL' );

					// Required fields go optional
					// These only go required when countryFrom is the same as countryTo
					(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['address'].reset();
					(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['address'].removeValidators( Validators.required );
					(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['address'].updateValueAndValidity();

					(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['postcode'].reset();
					(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['postcode'].removeValidators( Validators.required );
					(<UntypedFormGroup>this.mainForm.controls['movingTo'] ).controls['postcode'].updateValueAndValidity();

					// This variable is true when we had the (optional) label in the first place
					// The (optional) label gets added in the first place when the movingTo extended is enabled through the env setting and not the step0 data
					if( this.movingToExtendedOptionalLabelsStartingValue ) {

						// If we need to reset the labels to get (optional)
						movingTo.controls['_extendedOptional'].setValue( true );
					}
				}
			}

			var movingFrom = (<UntypedFormGroup>this.mainForm.get( 'movingFrom' ) );
			var movingTo = (<UntypedFormGroup>this.mainForm.get( 'movingTo' ) );

			var countryFromValue = movingFrom.controls['country'].value;
			var countryToValue = movingTo.controls['country'].value;

			// In this case we want to check if the countries are the same
			// Check if we have a value in movingFrom so they are both not empty in the working scenario
			if( ( countryFromValue && countryFromValue.length > 0 ) && countryFromValue === countryToValue ) {

				// Check if it's not extended already
				if( ! movingTo.controls['_isExtended'].value ) {

					console.log( 'movingTo Extended ENABLED' );

					// Set extended
					movingTo.controls['_isExtended'].setValue( true );
				}

				// We only have the movingToExtendedEnabled for Sirelo's
				// For SEA sites we want to keep the movingTo address & postcode optional
				if( this.domainData?.movingToExtendedEnabled ) {

					// Make it required
					makeMovingToRequiredOrOptional( 'required' );
				}
				else {

					// (optional) labels
					movingTo.controls['_extendedOptional'].setValue( true );
				}
			}
			else {

				// Country values are not the same now or already

				// If it is enabled
				// We only want to disable things if they were actually enabled
				if( movingTo.controls['_isExtended'].value ) {

					// If it's not enabled through the env setting
					if( ! this.domainData?.movingToExtendedEnabled ) {

						// Disable it
						movingTo.controls['_isExtended'].setValue( false );

						console.log( 'Extended movingTo DISABLED' );
					}

					makeMovingToRequiredOrOptional( 'optional' );
				}
			}
		}

	}

	// === HOUSE SIZE FIELD === //
	private initialPropertyRoomsFieldCheck() {

		if( ! this.mainForm.controls['_propertyTypeFromEnabled'].value ) {

			return;
		}

		var propertyRoomsFieldEnabled = false;

		// Check if propertyRooms is enabled
		if( this.domainData?.propertyRoomsEnabled ) {

			propertyRoomsFieldEnabled = true;
		}

		if( propertyRoomsFieldEnabled ) {

			console.log( 'Enable `propertyRooms` field' );
			this.mainForm.controls['_propertyRoomsEnabled'].setValue( true );
			(<UntypedFormGroup>this.mainForm.controls['propertyTypeFrom'] ).controls['propertyRooms'].addValidators( Validators.required );
		}
	}

	// === PREMATCH === //

	public moversConsentUpdate$ = new Subject<void>();

	private askPrematch() {

		// Check if either the country From or country To is US
		var fromVal = this.verifyCountryCode( (<UntypedFormGroup>this.mainForm.controls['movingFrom']).controls['country'].value );
		var toVal = this.verifyCountryCode( (<UntypedFormGroup>this.mainForm.controls['movingTo']).controls['country'].value );
		var isEligibleCountry = fromVal === 'US' || toVal === 'US';

		isEligibleCountry = true;

		if( ! isEligibleCountry ) {

			this.disableConsentMovers();

			return;
		}

		// Enable the step
		this.enableConsentMovers();

		// Add loading here
		this.fccLoading = true;

		// Should not be allowed to click continue while still loading

		console.log( 'Country is Eligible for prematch' );

		// Put the request in here
		var body = JSON.stringify( this.mainForm.value );

		// Send data to ERP
		this.apiService.postAskPrematch(body).subscribe( {
			next: (movers) => {

				// response has reply
				console.log( 'reply' );
				console.log( movers );

				// Parse the response if it's a string
				const moversArray = typeof movers === 'string' ? JSON.parse(movers) : movers;

				// Get both form arrays
				var moversFormArray = this.mainForm.get( 'consentMovers.movers' ) as UntypedFormArray;
				var originalMoversFormArray = this.mainForm.get( 'consentMovers._originalMovers' ) as UntypedFormArray;

				// Clear both arrays
				moversFormArray.clear();
				originalMoversFormArray.clear();

				// Create the form
				moversArray.forEach( (mover: Mover) => {
					const moverFormGroup = new UntypedFormGroup({
						checkbox: new UntypedFormControl(mover.checked),
						_id: new UntypedFormControl(mover.id),
						_name: new UntypedFormControl(mover.name),
						_logo: new UntypedFormControl(mover.logo),
						_rating: new UntypedFormControl(mover.rating),
						_amount: new UntypedFormControl(mover.rating_amount),
						_city: new UntypedFormControl(mover.city)
					});

					// Add to both form arrays
					moversFormArray.push(moverFormGroup);
					originalMoversFormArray.push(moverFormGroup);
				});

				// If the mover array has items, enable consent movers
				if( moversArray.length > 0 ) {

					// Emit an event to notify the consent movers component to update
					this.moversConsentUpdate$.next();

					// Set _fccEnabled to true
					this.mainForm.controls['_fccEnabled'].setValue( true );
				}
				else {

					// Disable consent movers if no movers
					this.disableConsentMovers();

					// Set _fccEnabled to false
					this.mainForm.controls['_fccEnabled'].setValue( false );
				}
			},
			error: (error) => {

				console.error('Error in askPrematch:', error);
				// Disable consent movers on error
				this.disableConsentMovers();
				this.mainForm.controls['_fccEnabled'].setValue(false);
			},
			complete: () => {
				// Stop loading animation in both success and error cases
				this.fccLoading = false;
			}
		});
	}

	private enableConsentMovers() {

		// If consentMovers is already enabled
		if( this.mainForm.controls['_consentMoversEnabled'].value ) {

			console.log( 'Consent Movers step was already enabled' );

			return;
		}

		console.log( 'Enable Consent Movers step' );

		// Set the next step for moving date to the consent step
		(<UntypedFormGroup>this.mainForm.controls['movingDate'] ).controls['_next'].setValue( 'consentMovers' );

		// Set the previous step for the name email phone to consent step
		(<UntypedFormGroup>this.mainForm.controls['nameEmailPhone'] ).controls['_previous'].setValue( 'consentMovers' );

		// Add one to total step amount
		this.totalStepsAmount++;

		// Validator is added after the consentMovers component has mover data

		// Set to enabled
		(<UntypedFormGroup>this.mainForm ).controls['_consentMoversEnabled'].setValue( true );
	}

	private disableConsentMovers() {

		if( ! this.mainForm.controls['_consentMoversEnabled'].value ) {

			console.log( 'Consent Movers step was already disabled' );

			return;
		}

		// Reset the next step for moving date to the name email phone step
		(<UntypedFormGroup>this.mainForm.controls['movingDate'] ).controls['_next'].setValue( 'nameEmailPhone' );

		// Reset the previous step for the name email phone to consent step
		(<UntypedFormGroup>this.mainForm.controls['nameEmailPhone'] ).controls['_previous'].setValue( 'movingDate' );

		// Subtract one to total step amount
		this.totalStepsAmount--;

		// Remove the validator from the consentMovers form group
		const consentMovers = this.mainForm.get('consentMovers') as UntypedFormGroup;
		consentMovers.clearValidators();
		consentMovers.updateValueAndValidity();

		// Set to disabled
		(<UntypedFormGroup>this.mainForm ).controls['_consentMoversEnabled'].setValue( false );
	}


	// === PREFILL MOVING FROM === //

	private checkForPrefillValues() {

		if( this.domainData?.preFillCountryFrom && this.domainData?.preFillCountryFrom.length > 0 ) {

			// Prefill country
			(<UntypedFormGroup>this.mainForm.get( 'movingFrom' ) ).controls['country'].setValue( this.domainData.preFillCountryFrom );
			(<UntypedFormGroup>this.mainForm.get( 'movingFrom' ) ).controls['country'].markAsDirty();
		}
	}

	// === AI REMARKS === //
	private dataToMistral( data: any ) {

		try {

			if( this.aiMistralDone ) {

				return;
			}

			if( data ) {

				this.aiMistralDone = true;
				const startTime = performance.now();

				// Pass this value to the postcode api
				this.apiService.getMistralAIResult( data )
					.pipe(take(1))
					.subscribe( {
					next: response => {
						try {

							const endTime = performance.now();
							const elapsedTime = (endTime - startTime).toFixed(2); // in ms
							const answer = JSON.parse( response );

							if( answer ) {

								console.log( answer )
								console.log( 'Request took: ' + elapsedTime );

								// If answer['remarks'] is not empty
								if( answer['remarks'] ) {

									this.mainForm.controls['_mistralRemarksDuration']?.setValue( elapsedTime );
									this.mainForm.controls['_mistralRemarksClarification']?.setValue( answer['remarks']['Clarification needed'] );
									this.mainForm.controls['_mistralRemarksReasoning']?.setValue( answer['remarks']['Reasoning'] );
								}

								// If answer['volume'] is not empty
								if( answer['volume'] ) {

									this.mainForm.controls['_mistralVolumeRemarksClarification']?.setValue( answer['volume']['Clarification needed'] );
									this.mainForm.controls['_mistralVolumeRemarksReasoning']?.setValue( answer['volume']['Reasoning'] );
								}

								// Log all form values
								console.log( this.mainForm.value );
							}

						} catch (innerError) {

						}
					},
					error: error => {

					}
				} );
			}
		} catch (error) {

		}


	}
}
